import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useCreateCustomerMutation } from '../../api/customerApi';
import type { CustomerCreateRequest } from '../../types/customer';

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  color: #718096;
  margin: 0 0 16px 0;
  font-size: 14px;
`;

const ModalBody = styled.div`
  padding: 0 24px 24px 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
`;

const RequiredLabel = styled(Label)`
  &:after {
    content: " *";
    color: #e53e3e;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:invalid {
    border-color: #e53e3e;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #4a5568;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  
  ${({ variant = 'secondary' }) => {
    if (variant === 'primary') {
      return `
        background: #4299e1;
        color: white;
        &:hover:not(:disabled) {
          background: #3182ce;
        }
        &:disabled {
          background: #a0aec0;
          cursor: not-allowed;
        }
      `;
    }
    return `
      background: white;
      color: #4a5568;
      border-color: #e2e8f0;
      &:hover {
        background: #f7fafc;
      }
    `;
  }}
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #742a2a;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #e53e3e;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background: #c6f6d5;
  color: #22543d;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #38a169;
  font-size: 14px;
`;

interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data: any) => void;
}

export const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [createCustomer, { isLoading, error }] = useCreateCustomerMutation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<CustomerCreateRequest>({
    name: '',
    email: '',
    company: '',
    domain: '',
    plan: 'starter',
    customDomain: '',
    billingEmail: '',
    contactPerson: '',
    createUser: true,
    userPassword: '',
    userName: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, userPassword: password }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const requestData = { ...formData };
      
      // Clean up empty optional fields
      if (!requestData.customDomain) delete requestData.customDomain;
      if (!requestData.billingEmail) delete requestData.billingEmail;
      if (!requestData.contactPerson) delete requestData.contactPerson;
      if (!requestData.createUser) {
        delete requestData.userPassword;
        delete requestData.userName;
      }

      const result = await createCustomer(requestData).unwrap();
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onSuccess?.(result);
        onClose();
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          domain: '',
          plan: 'starter',
          customDomain: '',
          billingEmail: '',
          contactPerson: '',
          createUser: true,
          userPassword: '',
          userName: '',
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to create customer:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Overlay isOpen={isOpen} onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Create New Customer</ModalTitle>
          <ModalSubtitle>
            Add a new customer to the system. Optionally create a user account for immediate access.
          </ModalSubtitle>
        </ModalHeader>
        
        <ModalBody>
          {error && (
            <ErrorMessage>
              {(error as any).data?.error || 'Failed to create customer. Please try again.'}
            </ErrorMessage>
          )}
          
          {showSuccess && (
            <SuccessMessage>
              Customer created successfully! {formData.createUser && 'User account has been created.'}
            </SuccessMessage>
          )}
          
          <Form onSubmit={handleSubmit}>
            <Section>
              <SectionTitle>Customer Information</SectionTitle>
              <FormRow>
                <FormGroup>
                  <RequiredLabel htmlFor="name">Customer Name</RequiredLabel>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                  />
                </FormGroup>
                <FormGroup>
                  <RequiredLabel htmlFor="email">Email Address</RequiredLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@company.com"
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <RequiredLabel htmlFor="company">Company Name</RequiredLabel>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    required
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="ACME Corporation"
                  />
                </FormGroup>
                <FormGroup>
                  <RequiredLabel htmlFor="domain">Domain</RequiredLabel>
                  <Input
                    id="domain"
                    name="domain"
                    type="text"
                    required
                    value={formData.domain}
                    onChange={handleInputChange}
                    placeholder="acme (alphanumeric only)"
                    pattern="[a-zA-Z0-9]+"
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <RequiredLabel htmlFor="plan">Plan</RequiredLabel>
                  <Select
                    id="plan"
                    name="plan"
                    value={formData.plan}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="customDomain">Custom Domain</Label>
                  <Input
                    id="customDomain"
                    name="customDomain"
                    type="text"
                    value={formData.customDomain}
                    onChange={handleInputChange}
                    placeholder="custom.example.com"
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label htmlFor="billingEmail">Billing Email</Label>
                  <Input
                    id="billingEmail"
                    name="billingEmail"
                    type="email"
                    value={formData.billingEmail}
                    onChange={handleInputChange}
                    placeholder="billing@company.com"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="contactPerson">Contact Person</Label>
                  <Input
                    id="contactPerson"
                    name="contactPerson"
                    type="text"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder="Jane Doe"
                  />
                </FormGroup>
              </FormRow>
            </Section>
            
            <Section>
              <SectionTitle>User Account (Optional)</SectionTitle>
              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="createUser"
                    checked={formData.createUser}
                    onChange={handleInputChange}
                  />
                  Create user account for this customer
                </CheckboxLabel>
              </FormGroup>
              
              {formData.createUser && (
                <>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="userName">User Name</Label>
                      <Input
                        id="userName"
                        name="userName"
                        type="text"
                        value={formData.userName}
                        onChange={handleInputChange}
                        placeholder="Leave empty to use customer name"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="userPassword">Password</Label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Input
                          id="userPassword"
                          name="userPassword"
                          type="text"
                          value={formData.userPassword}
                          onChange={handleInputChange}
                          placeholder="Leave empty for auto-generated"
                          style={{ flex: 1 }}
                        />
                        <Button
                          type="button"
                          onClick={generatePassword}
                          style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}
                        >
                          Generate
                        </Button>
                      </div>
                    </FormGroup>
                  </FormRow>
                </>
              )}
            </Section>
            
            <ButtonGroup>
              <Button type="button" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Customer'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalBody>
      </Modal>
    </Overlay>
  );
};